import React, { useEffect, useRef, useState } from 'react';
import '@crm/components/WondertradeLogin.css';

export type CccLoginHandlers = {
  login: (email: string, password: string) => Promise<{
    success: boolean;
    challenge?: string;
    session?: string;
    message?: string;
  }>;
  setNewPassword: (
    session: string,
    newPassword: string,
    email?: string
  ) => Promise<{ success: boolean; message?: string }>;
};

type Props = {
  auth: CccLoginHandlers;
  onSuccess: () => void;
};

/**
 * CCC-only login form — no @crm auth/context imports (avoids Cognito client init on render).
 */
const CccLoginForm: React.FC<Props> = ({ auth, onSuccess }) => {
  const loginFn = auth?.login;
  const setPwdFn = auth?.setNewPassword;

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'login' | 'new-password'>('login');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challengeSession, setChallengeSession] = useState<string | undefined>('');
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const quoteTextRef = useRef<HTMLDivElement>(null);
  const quoteAuthorRef = useRef<HTMLDivElement>(null);

  const quotes = [
    { text: 'Changing the future everyday', author: 'Wondertrade' },
    { text: 'The quality of your life is the quality of your relationships.', author: 'Tony Robbins' },
    { text: 'Success is not final, failure is not fatal: it is the courage to continue that counts.', author: 'Winston Churchill' },
  ];

  useEffect(() => {
    const rotateQuote = () => {
      const textEl = quoteTextRef.current;
      const authorEl = quoteAuthorRef.current;
      if (!textEl || !authorEl) return;
      setIsVisible(false);
      textEl.style.opacity = '0';
      authorEl.style.opacity = '0';
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
        setIsVisible(true);
        if (quoteTextRef.current) quoteTextRef.current.style.opacity = '1';
        if (quoteAuthorRef.current) quoteAuthorRef.current.style.opacity = '1';
      }, 500);
    };
    const interval = setInterval(rotateQuote, 12000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  if (typeof loginFn !== 'function' || typeof setPwdFn !== 'function') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'Inter, system-ui, sans-serif',
          background: '#f8fafc',
        }}
      >
        <div
          style={{
            maxWidth: 420,
            padding: 24,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#0f172a' }}>
            Autenticação indisponível
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
            O serviço de login não está pronto. Recarregue a página ou contacte o suporte.
          </p>
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await loginFn(email, password);
      if (result?.challenge === 'NEW_PASSWORD_REQUIRED' && result.session) {
        setChallengeSession(result.session);
        setStep('new-password');
      } else if (result?.success) {
        onSuccess();
      } else {
        setError(result?.message || 'Credenciais inválidas. Tente novamente.');
      }
    } catch (err: unknown) {
      const e = err as { message?: string; code?: string; response?: unknown };
      const isNetwork = !e?.response && (e?.code === 'ERR_NETWORK' || String(e?.message).includes('Network'));
      setError(
        isNetwork
          ? 'Não foi possível contactar o servidor. Verifique a ligação ou tente mais tarde.'
          : e?.message || 'Erro no login. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('As palavras-passe não coincidem.');
      return;
    }
    if (!challengeSession) {
      setError('Sessão inválida para definir nova palavra-passe.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await setPwdFn(challengeSession, newPassword, email);
      if (result?.success) {
        onSuccess();
      } else {
        setError(result?.message || 'Erro ao definir nova palavra-passe.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e?.response?.data?.message || 'Erro ao definir nova palavra-passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="wondertrade-login-container"
      style={{
        backgroundImage: 'url(/Banner_Candidaturas.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="login-card">
        <div className="logo-section">
          <div className="logo">
            <img src="/LOGO-footer-Wondertrade.png" alt="Wondertrade Logo" className="logo-image" />
          </div>
          <p className="brand-subtitle">Aceda à sua conta</p>
        </div>

        {error ? (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        ) : null}

        {step === 'login' ? (
          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label" htmlFor="ccc-email">
                Email
              </label>
              <input
                type="email"
                id="ccc-email"
                className="form-input"
                placeholder="Introduza o seu email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ccc-password">
                Palavra-passe
              </label>
              <input
                type="password"
                id="ccc-password"
                className="form-input"
                placeholder="Introduza a sua palavra-passe"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'A Iniciar Sessão...' : 'Iniciar Sessão'}
            </button>
          </form>
        ) : (
          <div className="totp-section">
            <button
              type="button"
              className="back-button"
              onClick={() => {
                setStep('login');
                setError('');
                setChallengeSession(undefined);
              }}
            >
              ← Voltar ao Login
            </button>
            <form className="login-form" onSubmit={handleSetNewPassword}>
              <div className="form-group">
                <label className="form-label" htmlFor="ccc-new-password">
                  Nova Palavra-passe
                </label>
                <input
                  type="password"
                  id="ccc-new-password"
                  className="form-input"
                  value={newPassword}
                  onChange={(ev) => setNewPasswordValue(ev.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="ccc-confirm-password">
                  Confirmar Palavra-passe
                </label>
                <input
                  type="password"
                  id="ccc-confirm-password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'A Definir…' : 'Definir'}
              </button>
            </form>
          </div>
        )}

        <div className="quote-rotator">
          <div className="quote-text" ref={quoteTextRef} style={{ opacity: isVisible ? 1 : 0 }}>
            &quot;{quotes[currentQuoteIndex]?.text ?? ''}&quot;
          </div>
          <div className="quote-author" ref={quoteAuthorRef} style={{ opacity: isVisible ? 1 : 0 }}>
            — {quotes[currentQuoteIndex]?.author ?? ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CccLoginForm;
